import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { SignUpUserDocument } from '@graphql/types';
import { useCallback } from 'react';

import type { SignUpResponse, SignUpUserInput } from '../types';

export const useSignUp = () => {
  const [signUpUserMutation, { loading: signUpLoading }] = useMutation(SignUpUserDocument);
  /**
   * Executes the signup mutation and returns the payload or throws.
   */
  const doSignUp = useCallback(
    async (data: SignUpUserInput): Promise<SignUpResponse> => {
      try {
        const response = await signUpUserMutation({
          variables: { input: data },
        });

        const signUpUserRes = response.data?.signUpUser;

        if (!signUpUserRes) {
          notify.error({ title: 'Signup failed', description: 'No response from server' });
          throw new Error('Signup failed: No data returned from server');
        }

        const hasErrors = signUpUserRes.errors && signUpUserRes.errors.length > 0;

        if (hasErrors) {
          notify.error({ title: signUpUserRes.errors?.[0]?.code, description: signUpUserRes.errors?.[0]?.message });
        } else {
          notify.success({ title: 'Signup successful' });
        }

        return signUpUserRes as SignUpResponse;
      } catch (error: any) {
        // Re-throw Apollo errors with more context
        if (error.networkError) {
          throw new Error(`Network error: ${error.networkError.message || 'Unable to connect to server'}`);
        }

        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const errorMessages = error.graphQLErrors.map((err: any) => err.message).join(', ');

          throw new Error(`GraphQL error: ${errorMessages}`);
        }

        // Re-throw if it's already an Error instance
        if (error instanceof Error) {
          throw error;
        }

        // Otherwise wrap in Error
        throw new Error(error?.message || 'Signup failed');
      }
    },
    [signUpUserMutation]
  );

  return { doSignUp, signUpLoading };
};
